/**
 * Intake format & size policy — ONE source of truth shared by the client
 * (pre-flight, drag/drop, review step) and mirrored by the edge validators and
 * the storage bucket settings (see migration 20261003).
 *
 * Keep the extension/MIME lists here in lock-step with:
 *   - supabase/functions/prepare-resume-upload / prepare-rfp-upload
 *   - supabase/functions/finalize-resume-upload / finalize-rfp-upload
 *   - the `resumes` / `documents` bucket allowed_mime_types
 */

export type IntakeCategory = 'recruiting' | 'solicitation';

export interface FormatPolicy {
  category: IntakeCategory;
  label: string;
  /** Max bytes for a single file. */
  maxBytes: number;
  /** Max files in one submission. */
  maxFiles: number;
  /** Allowed lower-case extensions. */
  extensions: string[];
  /** Bucket MIME allow-list for these formats. */
  mimes: string[];
}

const MIME = {
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  csv: 'text/csv',
  txt: 'text/plain',
  rtf: 'application/rtf',
} as const;

export const BATCH_MAX_FILES = 10;
export const RESUME_MAX_BYTES = 10 * 1024 * 1024; // 10 MB per resume / supporting file
export const SOLICITATION_MAX_BYTES = 20 * 1024 * 1024; // 20 MB per RFP / solicitation file

export const RECRUITING_POLICY: FormatPolicy = {
  category: 'recruiting',
  label: 'Resume & supporting documents',
  maxBytes: RESUME_MAX_BYTES,
  maxFiles: BATCH_MAX_FILES,
  extensions: ['pdf', 'doc', 'docx', 'txt', 'rtf'],
  mimes: [MIME.pdf, MIME.doc, MIME.docx, MIME.txt, MIME.rtf, 'text/rtf'],
};

export const SOLICITATION_POLICY: FormatPolicy = {
  category: 'solicitation',
  label: 'RFP / solicitation documents',
  maxBytes: SOLICITATION_MAX_BYTES,
  maxFiles: BATCH_MAX_FILES,
  extensions: ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv', 'pptx', 'txt'],
  mimes: [MIME.pdf, MIME.doc, MIME.docx, MIME.xls, MIME.xlsx, MIME.pptx, MIME.csv, MIME.txt],
};

export function policyFor(category: IntakeCategory): FormatPolicy {
  return category === 'solicitation' ? SOLICITATION_POLICY : RECRUITING_POLICY;
}

/** Lower-case extension (no dot), or '' when there is none. */
export function extensionOf(filename: string): string {
  const clean = (filename || '').trim();
  const idx = clean.lastIndexOf('.');
  if (idx < 0 || idx === clean.length - 1) return '';
  return clean.slice(idx + 1).toLowerCase();
}

export function humanSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export interface DeclarationResult {
  ok: boolean;
  error?: string;
  /** Stable code so the UI can translate the message. */
  code?: 'noExt' | 'macro' | 'format' | 'empty' | 'tooLarge';
  /** Interpolation values for the translated message. */
  formats?: string;
  mb?: number;
}

/**
 * Cheap pre-flight check used BEFORE requesting a signed upload URL (client and
 * prepare-* functions). The real signature/structure validation happens
 * server-side in the finalize step.
 */
export function validateDeclaration(category: IntakeCategory, filename: string, sizeBytes: number): DeclarationResult {
  const policy = policyFor(category);
  const ext = extensionOf(filename);
  if (!ext) return { ok: false, code: 'noExt', error: 'The file needs a name with a recognised extension.' };
  if (ext === 'docm' || ext === 'xlsm' || ext === 'pptm' || ext === 'xlsb') {
    return { ok: false, code: 'macro', error: 'Macro-enabled files are not accepted. Please save as a macro-free format.' };
  }
  if (!policy.extensions.includes(ext)) {
    const formats = policy.extensions.map((e) => e.toUpperCase()).join(', ');
    return { ok: false, code: 'format', formats, error: `Allowed formats are ${formats}.` };
  }
  if (sizeBytes <= 0) return { ok: false, code: 'empty', error: 'The file appears to be empty.' };
  if (sizeBytes > policy.maxBytes) {
    const mb = Math.round(policy.maxBytes / (1024 * 1024));
    return { ok: false, code: 'tooLarge', mb, error: `Files must be ${mb} MB or smaller.` };
  }
  return { ok: true };
}

/** Human-readable accept string for <input accept="…">. */
export function acceptAttr(category: IntakeCategory): string {
  return policyFor(category).extensions.map((e) => `.${e}`).join(',');
}